import { useState, useCallback, useMemo, useRef, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import TasksDashboardOrganism from 'components/organisms/TasksDashboard';
import EditTaskModal from 'components/organisms/TasksDashboard/modals/EditTaskModal';
import ReturnToPoolModal from 'components/organisms/TasksDashboard/modals/ReturnToPoolModal';
import { useDebouncedEffect } from 'infrastructure/hooks/utils/use-debounced-effect';

import { TAB_KEYS, DEFAULT_PAGE_LIMIT } from './constants';
import { useAssignDashboardTask } from './hooks/use-assign-task';
import { useCompleteDashboardTask } from './hooks/use-complete-dashboard-task';
import { useDashboardTasks } from './hooks/use-dashboard-tasks';
import { useTabCounts } from './hooks/use-tab-counts';
import { useEditDashboardTask } from './hooks/use-edit-dashboard-task';
import { useMinimumLoading } from './hooks/use-minimum-loading';
import { useUnassignDashboardTask } from './hooks/use-unassign-dashboard-task';

import type { FC } from 'react';
import type { IEditTaskPayload, SortField, SortOrder, TabKey } from './types';

const DEBOUNCE_MS = 300;

const isValidTab = (tab: string | null): tab is TabKey =>
  tab === TAB_KEYS.OPEN_POOL ||
  tab === TAB_KEYS.ASSIGNED_TO_ME ||
  tab === TAB_KEYS.COMPLETED;

const TasksDashboard: FC = () => {
  const [searchParams, setSearchParams] = useSearchParams();

  const initialTab = searchParams.get('tab');
  const [activeTab, setActiveTab] = useState<TabKey>(
    isValidTab(initialTab) ? initialTab : TAB_KEYS.ASSIGNED_TO_ME,
  );
  const [offset, setOffset] = useState(0);
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [podFilter, setPodFilter] = useState<string | undefined>();
  const [priorityFilter, setPriorityFilter] = useState<string | undefined>();
  const [reporterFilter, setReporterFilter] = useState<string | undefined>();
  const [sortBy, setSortBy] = useState<SortField | undefined>();
  const [sortOrder, setSortOrder] = useState<SortOrder | undefined>();

  useDebouncedEffect({
    callback: () => setDebouncedSearch(search),
    value: search,
    delay: DEBOUNCE_MS,
  });

  const hasActiveFilters = !!(
    debouncedSearch ||
    podFilter ||
    priorityFilter ||
    reporterFilter
  );

  const { data, isLoading, isFetching, isPreviousData, isError, refetch } =
    useDashboardTasks({
      tab: activeTab,
      search: debouncedSearch || undefined,
      podFilter,
      priority: priorityFilter,
      reporterFilter,
      sortBy,
      sortOrder,
      limit: DEFAULT_PAGE_LIMIT,
      offset,
    });

  const showLoading = useMinimumLoading(
    isLoading || (isFetching && isPreviousData),
  );
  const { data: tabCounts } = useTabCounts({
    search: debouncedSearch || undefined,
    podFilter,
    priority: priorityFilter,
    reporterFilter,
    limit: DEFAULT_PAGE_LIMIT,
    offset,
  });

  const assignMutation = useAssignDashboardTask();
  const completeMutation = useCompleteDashboardTask();
  const unassignMutation = useUnassignDashboardTask();
  const editMutation = useEditDashboardTask();

  useEffect(() => {
    return () => {
      assignMutation.reset();
      completeMutation.reset();
      unassignMutation.reset();
      editMutation.reset();
    };
  }, []);

  const [assigningGuids, setAssigningGuids] = useState<Set<string>>(
    () => new Set(),
  );
  const assigningRef = useRef(new Set<string>());

  const handleAssign = useCallback(
    (taskGuid: string) => {
      if (assigningRef.current.has(taskGuid)) return;
      assigningRef.current.add(taskGuid);
      setAssigningGuids((prev) => new Set(prev).add(taskGuid));
      assignMutation.mutate(taskGuid, {
        onSettled: () => {
          assigningRef.current.delete(taskGuid);
          setAssigningGuids((prev) => {
            const next = new Set(prev);
            next.delete(taskGuid);
            return next;
          });
        },
      });
    },
    [assignMutation],
  );

  const [completingGuids, setCompletingGuids] = useState<Set<string>>(
    () => new Set(),
  );
  const completingRef = useRef(new Set<string>());

  const handleComplete = useCallback(
    (taskGuid: string) => {
      if (completingRef.current.has(taskGuid)) return;
      completingRef.current.add(taskGuid);
      setCompletingGuids((prev) => new Set(prev).add(taskGuid));
      completeMutation.mutate(taskGuid, {
        onSettled: () => {
          completingRef.current.delete(taskGuid);
          setCompletingGuids((prev) => {
            const next = new Set(prev);
            next.delete(taskGuid);
            return next;
          });
        },
      });
    },
    [completeMutation],
  );

  const [returnModalOpen, setReturnModalOpen] = useState(false);
  const [returnTargetGuid, setReturnTargetGuid] = useState<string | null>(null);
  const [returnSubmitError, setReturnSubmitError] = useState(false);

  const handleReturnOpen = useCallback((taskGuid: string) => {
    setReturnTargetGuid(taskGuid);
    setReturnSubmitError(false);
    setReturnModalOpen(true);
  }, []);

  const handleReturnSubmit = useCallback(
    (returnNote: string) => {
      if (!returnTargetGuid) return;
      setReturnSubmitError(false);
      unassignMutation.mutate(
        { taskGuid: returnTargetGuid, returnNote },
        {
          onSuccess: () => {
            setReturnModalOpen(false);
            setReturnTargetGuid(null);
          },
          onError: () => {
            setReturnSubmitError(true);
          },
        },
      );
    },
    [returnTargetGuid, unassignMutation],
  );

  const handleReturnCancel = useCallback(() => {
    setReturnModalOpen(false);
    setReturnTargetGuid(null);
  }, []);

  const [editModalOpen, setEditModalOpen] = useState(false);
  const [editTargetGuid, setEditTargetGuid] = useState<string | null>(null);
  const [editSubmitError, setEditSubmitError] = useState(false);

  const handleEditOpen = useCallback(
    (taskGuid: string) => {
      const task = data?.tasks.find((t) => t.guid === taskGuid);
      if (!task) return;
      setEditTargetGuid(taskGuid);
      setEditSubmitError(false);
      setEditModalOpen(true);
    },
    [data?.tasks],
  );

  const editTargetTask = useMemo(
    () => data?.tasks.find((t) => t.guid === editTargetGuid),
    [data?.tasks, editTargetGuid],
  );

  const editInitialValues = useMemo(
    () => ({
      title: editTargetTask?.title ?? '',
      priority: editTargetTask?.priority ?? ('medium' as const),
    }),
    [editTargetTask],
  );

  const handleEditSubmit = useCallback(
    (payload: IEditTaskPayload) => {
      if (!editTargetGuid) return;
      setEditSubmitError(false);
      editMutation.mutate(
        { taskGuid: editTargetGuid, payload },
        {
          onSuccess: () => {
            setEditModalOpen(false);
            setEditTargetGuid(null);
          },
          onError: () => {
            setEditSubmitError(true);
          },
        },
      );
    },
    [editTargetGuid, editMutation],
  );

  const handleEditCancel = useCallback(() => {
    setEditModalOpen(false);
    setEditTargetGuid(null);
  }, []);

  const handleSortChange = useCallback(
    (field: SortField | undefined, order: SortOrder | undefined) => {
      setSortBy(field);
      setSortOrder(order);
      setOffset(0);
    },
    [],
  );

  const handleTabChange = useCallback(
    (tabKey: TabKey) => {
      setActiveTab(tabKey);
      setSearchParams({ tab: tabKey });
      setOffset(0);
      setSortBy(undefined);
      setSortOrder(undefined);
    },
    [setSearchParams],
  );

  const handlePageChange = useCallback((newOffset: number) => {
    setOffset(newOffset);
  }, []);

  const handleSearchChange = useCallback((value: string) => {
    setSearch(value);
    setOffset(0);
  }, []);

  const handlePodFilterChange = useCallback((value: string | undefined) => {
    setPodFilter(value);
    setOffset(0);
  }, []);

  const handlePriorityFilterChange = useCallback(
    (value: string | undefined) => {
      setPriorityFilter(value);
      setOffset(0);
    },
    [],
  );

  const handleReporterFilterChange = useCallback(
    (value: string | undefined) => {
      setReporterFilter(value);
      setOffset(0);
    },
    [],
  );

  const handleClearFilters = useCallback(() => {
    setSearch('');
    setDebouncedSearch('');
    setPodFilter(undefined);
    setPriorityFilter(undefined);
    setReporterFilter(undefined);
    setSortBy(undefined);
    setSortOrder(undefined);
    setOffset(0);
  }, []);

  return (
    <>
      <TasksDashboardOrganism
        activeTab={activeTab}
        onTabChange={handleTabChange}
        data={data}
        isLoading={showLoading}
        isError={isError}
        onRetry={() => {
          refetch();
        }}
        hasActiveFilters={hasActiveFilters}
        onClearFilters={handleClearFilters}
        search={search}
        onSearchChange={handleSearchChange}
        podFilter={podFilter}
        onPodFilterChange={handlePodFilterChange}
        priorityFilter={priorityFilter}
        onPriorityFilterChange={handlePriorityFilterChange}
        reporterFilter={reporterFilter}
        onReporterFilterChange={handleReporterFilterChange}
        offset={offset}
        limit={DEFAULT_PAGE_LIMIT}
        onPageChange={handlePageChange}
        assigningGuids={assigningGuids}
        onAssign={handleAssign}
        completingGuids={completingGuids}
        onComplete={handleComplete}
        onReturn={handleReturnOpen}
        returningGuid={returnTargetGuid}
        onEdit={handleEditOpen}
        sortBy={sortBy}
        sortOrder={sortOrder}
        onSortChange={handleSortChange}
        tabCounts={tabCounts}
      />
      <ReturnToPoolModal
        open={returnModalOpen}
        isSubmitting={unassignMutation.isLoading}
        submitError={returnSubmitError}
        onSubmit={handleReturnSubmit}
        onCancel={handleReturnCancel}
      />
      <EditTaskModal
        open={editModalOpen}
        isSubmitting={editMutation.isLoading}
        submitError={editSubmitError}
        patientName={editTargetTask?.patientName ?? ''}
        initialValues={editInitialValues}
        onSubmit={handleEditSubmit}
        onCancel={handleEditCancel}
      />
    </>
  );
};

export default TasksDashboard;
